/**
 * SettingsFlow — 기기별 설정(사운드 볼륨/언어/표기형식)의 상태·영속·전역 반영 흐름 (Phaser-free).
 *
 * GameScene S4c 추출: 설정은 진행 SaveData와 분리된 로컬 전용 채널(BrickSettingsStore).
 * 뷰 전파(정적 라벨 재번역·RTL 미러·HUD 갱신)는 셸 콜백(onLanguageApplied 등)으로 주입 —
 * flow는 상태/저장/전역(setLanguage·setNumberFormat·soundController)만 소유한다.
 * 소유: settingsBoot이 생성·모달 결선, GameScene은 loadAndApply 시점 load만 호출.
 */
import { SOUND_CATEGORIES, type SoundCategory } from "@yjsgames/sound";
import {
  createDefaultBrickSettings,
  PERF_OVERLAY_AVAILABLE,
  PERF_OVERLAY_FORCED,
  type BrickSettings,
} from "../models/BrickSettings.js";
import { normalizeSettings } from "../systems/BrickSettingsStore.js";
import type { BrickSettingsStore } from "../systems/BrickSettingsStore.js";
import type { SoundController } from "../systems/SoundController.js";
import {
  DEFAULT_LANGUAGE,
  detectLanguage,
  type LanguageCode,
} from "../i18n/locales.js";
import { setLanguage, getTextDirection } from "../i18n/i18n.js";
import { setNumberFormat } from "../ui/formatNumber.js";
import {
  DEFAULT_NUMBER_FORMAT,
  type NumberFormat,
} from "../models/ModuleBrickSaveData.js";
import type { TargetFpsSetting } from "@yjsgames/perf";

const DEFAULT_MIGRATION_TIMEOUT_MS = 1500;

export interface SettingsFlowDeps {
  /** 설정 저장소 — ctx getter (applySaveData 교체 stale 방지). */
  store: () => BrickSettingsStore;
  soundController: () => SoundController;
  /** 구(舊) 진행 데이터 raw 로드 — language/numberFormat 1회 마이그레이션용. */
  loadRawSave: () => Promise<Record<string, unknown> | null>;
  /** 언어 전역 반영 후 뷰 전파(정적 라벨/RTL 미러/HUD) — 셸이 구현. */
  onLanguageApplied: (code: LanguageCode, dirChanged: boolean) => void;
  /** 표기형식 전역 반영 후 뷰 전파(설정 모달 표시/무효화) — 셸이 구현. */
  onNumberFormatApplied: (format: NumberFormat) => void;
  /** 목표 FPS 전역 반영 — 셸이 applyTargetFps(game)로 game.loop 런타임 조정 (발열/배터리 즉시 효과). */
  onTargetFpsApplied: (setting: TargetFpsSetting) => void;
  /** 구 진행 데이터 마이그레이션 timeout. 네이티브 저장 플러그인이 멈춰도 부팅은 계속되어야 한다. */
  migrationTimeoutMs?: number;
}

export class SettingsFlow {
  private volumes: Record<SoundCategory, number> =
    createDefaultBrickSettings().soundVolumes;
  private lang: LanguageCode = DEFAULT_LANGUAGE;
  private format: NumberFormat = DEFAULT_NUMBER_FORMAT;
  private fps: TargetFpsSetting = createDefaultBrickSettings().targetFps;
  private perfEnabled: boolean = createDefaultBrickSettings().perfOverlayEnabled;

  constructor(private readonly deps: SettingsFlowDeps) {}

  get soundVolumes(): Record<SoundCategory, number> {
    return this.volumes;
  }

  get language(): LanguageCode {
    return this.lang;
  }

  get numberFormat(): NumberFormat {
    return this.format;
  }

  get targetFps(): TargetFpsSetting {
    return this.fps;
  }

  get perfOverlayEnabled(): boolean {
    return this.perfEnabled;
  }

  /** 부팅 로드 — 설정 없으면 구 진행 데이터에서 1회 마이그레이션 후 저장. 전역(언어/표기/볼륨) 반영. */
  async loadAndApply(): Promise<void> {
    const store = this.deps.store();
    let settings = await store.load();
    if (settings === null) {
      settings = store.lastLoadFailed ? this.defaultSettingsForBoot() : await this.migrate();
      if (!store.lastLoadFailed) await store.save(settings);
    }
    this.apply(settings);
  }

  /**
   * 언어 변경 (설정 드롭다운) — scene.restart 없이 인플레이스 갱신(freeze 0).
   * 전역 setLanguage 후 dirChanged와 함께 셸 콜백 — 뷰 미러/재번역/HUD 갱신은 셸 소관.
   */
  setLanguageTo(code: LanguageCode): void {
    if (code === this.lang) return;
    const dirChanged = getTextDirection(this.lang) !== getTextDirection(code);
    this.lang = code;
    setLanguage(code);
    this.deps.onLanguageApplied(code, dirChanged);
    void this.save();
  }

  /** 사운드 카테고리(bgm/sfx/ui) 볼륨 변경 — 즉시 반영 + 로컬 저장. */
  setSoundVolume(category: SoundCategory, level: number): void {
    this.volumes = { ...this.volumes, [category]: level };
    this.deps.soundController().setCategoryLevel(category, level);
    void this.save();
  }

  /** 큰 숫자 표기 형식 변경 — 전역 반영 후 셸 콜백(숫자 재렌더는 표시 시점 무효화). */
  setNumberFormatTo(format: NumberFormat): void {
    if (format === this.format) return;
    this.format = format;
    setNumberFormat(format);
    this.deps.onNumberFormatApplied(format);
    void this.save();
  }

  /** 목표 FPS 변경 (설정 드롭다운) — 셸이 game.loop에 즉시 반영(onTargetFpsApplied). 발열/배터리 효과. */
  setTargetFpsTo(setting: TargetFpsSetting): void {
    if (setting === this.fps) return;
    this.fps = setting;
    this.deps.onTargetFpsApplied(setting);
    void this.save();
  }

  /**
   * 성능 오버레이 on/off 영속화.
   * 일반 빌드는 비활성이고, 진단 빌드는 true로 고정한다.
   */
  setPerfOverlayEnabledTo(on: boolean): void {
    const next = PERF_OVERLAY_FORCED ? true : PERF_OVERLAY_AVAILABLE && on;
    if (next === this.perfEnabled) return;
    this.perfEnabled = next;
    void this.save();
  }

  private async save(): Promise<void> {
    await this.deps.store().save({
      soundVolumes: this.volumes,
      language: this.lang,
      numberFormat: this.format,
      targetFps: this.fps,
      perfOverlayEnabled: this.perfEnabled,
    });
  }

  /** 구 진행 데이터(language/numberFormat) → BrickSettings 1회 이전. 구 데이터 없으면 navigator 감지. */
  private async migrate(): Promise<BrickSettings> {
    const raw = await this.loadRawSaveForMigration();
    const migrated = normalizeSettings({
      language: raw?.["language"],
      numberFormat: raw?.["numberFormat"],
    });
    if (raw?.["language"] === undefined) migrated.language = detectLanguage();
    return migrated;
  }

  private defaultSettingsForBoot(): BrickSettings {
    const settings = createDefaultBrickSettings();
    settings.language = detectLanguage();
    return settings;
  }

  private async loadRawSaveForMigration(): Promise<Record<string, unknown> | null> {
    try {
      return await withTimeout(
        this.deps.loadRawSave(),
        this.deps.migrationTimeoutMs ?? DEFAULT_MIGRATION_TIMEOUT_MS,
        "settings migration raw save load timed out",
      );
    } catch (error) {
      console.warn("[SettingsFlow] migration skipped:", (error as Error).message);
      return null;
    }
  }

  /** 설정값 전역 반영 — 볼륨/언어/표기. 뷰 전파 콜백은 호출하지 않음(부팅은 buildUi 전이라 뷰 부재). */
  private apply(settings: BrickSettings): void {
    this.volumes = { ...settings.soundVolumes };
    for (const cat of SOUND_CATEGORIES) {
      this.deps.soundController().setCategoryLevel(cat, this.volumes[cat]);
    }
    this.lang = settings.language;
    setLanguage(this.lang);
    this.format = settings.numberFormat;
    setNumberFormat(this.format);
    // game.loop fps는 main.ts createGame(부팅)이 이미 settings.targetFps로 캡 — apply는 상태 동기화만.
    this.fps = settings.targetFps;
    this.perfEnabled = PERF_OVERLAY_FORCED
      ? true
      : PERF_OVERLAY_AVAILABLE && settings.perfOverlayEnabled;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}
